import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type { DeviceSensorPayload, IsoString, WebSocketState } from '../types/index.js';

const getWsUrl = (): string => {
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL as string;
  }
  return 'wss://placeholder.execute-api.us-east-1.amazonaws.com/development';
};

const buildWsUrl = (idToken: string): string => {
  const base = getWsUrl();
  const apiKey =
    typeof import.meta !== 'undefined'
      ? (import.meta.env?.VITE_WS_API_KEY as string | undefined)
      : undefined;
  const params = new URLSearchParams({ access_token: idToken });
  if (apiKey) params.set('x-api-key', apiKey);
  return `${base}?${params.toString()}`;
};

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_BASE_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const PING_INTERVAL_MS = 30_000;
const MAX_MISSED_PONGS = 2;

// Maps raw AWS IoT shadow reported state to DeviceSensorPayload.
// Backend always sends temp in Celsius (temp_celsius). occupancy_state 0 = vacant, 1–3 = occupied.
function normalizeDeviceState(
  state: Record<string, unknown>,
  updatedAt: string,
): DeviceSensorPayload {
  return {
    outlet1: state.outlet1 === 1 || state.outlet1 === true,
    outlet2: state.outlet2 === 1 || state.outlet2 === true,
    temperature: parseFloat(String(state.temp_celsius ?? 0)),
    humidity: parseFloat(String(state.humidity ?? 0)),
    lux: parseFloat(String(state.lux ?? 0)),
    AQI: typeof state.AQI === 'number' ? state.AQI : 0,
    occupancy: Number(state.occupancy_state ?? 0) > 0,
    lastUpdated: updatedAt as IsoString,
    deviceStatus: 'online',
  };
}

interface InternalWebSocketState extends WebSocketState {
  _ws: WebSocket | null;
  _subscribedDeviceIds: string[];
  _reconnectAttempts: number;
  // Stored so reconnects always fetch a fresh token (avoids stale-token loops after 1h expiry).
  _getToken: (() => Promise<string>) | null;
  _pingInterval: ReturnType<typeof setInterval> | null;
  _missedPongs: number;
}

export const useWebSocketStore = create<InternalWebSocketState>((set, get) => ({
  connected: false,
  reconnecting: false,
  devices: {},
  _ws: null,
  _subscribedDeviceIds: [],
  _reconnectAttempts: 0,
  _getToken: null,
  _pingInterval: null,
  _missedPongs: 0,

  connect: (getToken: () => Promise<string>) => {
    set({ _getToken: getToken });
    const existing = get()._ws;
    if (existing) existing.close();

    void getToken()
      .then((idToken) => {
        const ws = new WebSocket(buildWsUrl(idToken));
        set({ _ws: ws, reconnecting: false });

        ws.onopen = () => {
          set({ connected: true, reconnecting: false, _reconnectAttempts: 0, _missedPongs: 0 });

          const { _subscribedDeviceIds } = get();
          if (_subscribedDeviceIds.length > 0) {
            ws.send(
              JSON.stringify({
                action: 'subscribeDevices',
                body: { deviceIds: _subscribedDeviceIds },
              }),
            );
          }

          // Heartbeat: ping every 30s, reconnect after 2 consecutive missed pongs.
          const interval = setInterval(() => {
            const { _ws: currentWs, _missedPongs } = get();
            if (!currentWs || currentWs.readyState !== WebSocket.OPEN) return;
            if (_missedPongs >= MAX_MISSED_PONGS) {
              clearInterval(interval);
              currentWs.close();
              return;
            }
            set((s) => ({ _missedPongs: s._missedPongs + 1 }));
            currentWs.send(JSON.stringify({ action: 'ping' }));
          }, PING_INTERVAL_MS);
          set({ _pingInterval: interval });
        };

        ws.onmessage = (event: MessageEvent) => {
          try {
            const raw: unknown = JSON.parse(event.data as string);
            if (typeof raw !== 'object' || raw === null || !('type' in raw)) return;
            const msg = raw as Record<string, unknown>;

            if (msg.type === 'pong') {
              set({ _missedPongs: 0 });
              return;
            }

            if (msg.type === 'device.state') {
              const deviceId = msg.deviceId as string;
              const state = (msg.state ?? {}) as Record<string, unknown>;
              const updatedAt = (msg.updatedAt as string | undefined) ?? new Date().toISOString();
              set((s) => ({
                devices: { ...s.devices, [deviceId]: normalizeDeviceState(state, updatedAt) },
              }));
            }
            // subscription.updated is informational — no client action needed.
          } catch {
            // malformed frame — ignore silently
          }
        };

        ws.onclose = () => {
          const { _pingInterval } = get();
          if (_pingInterval) clearInterval(_pingInterval);
          set({ connected: false, _ws: null, _pingInterval: null, _missedPongs: 0 });

          const { _reconnectAttempts, _getToken } = get();
          if (_reconnectAttempts < MAX_RECONNECT_ATTEMPTS && _getToken) {
            const delay = Math.min(
              RECONNECT_BASE_DELAY_MS * 2 ** _reconnectAttempts,
              MAX_RECONNECT_DELAY_MS,
            );
            set({ reconnecting: true, _reconnectAttempts: _reconnectAttempts + 1 });
            // Re-read _getToken from store so each reconnect gets a fresh factory reference.
            setTimeout(() => void get().connect(get()._getToken!), delay);
          } else {
            set({ reconnecting: false });
          }
        };

        ws.onerror = () => {
          ws.close();
        };
      })
      .catch(() => {
        set({ reconnecting: false });
      });
  },

  disconnect: () => {
    const { _ws, _subscribedDeviceIds, _pingInterval } = get();
    if (_pingInterval) clearInterval(_pingInterval);
    if (_ws && _ws.readyState === WebSocket.OPEN) {
      _ws.send(
        JSON.stringify({ action: 'unsubscribeDevices', body: { deviceIds: _subscribedDeviceIds } }),
      );
      _ws.close();
    }
    set({
      connected: false,
      reconnecting: false,
      _ws: null,
      devices: {},
      _subscribedDeviceIds: [],
      _getToken: null,
      _pingInterval: null,
      _missedPongs: 0,
    });
  },

  subscribe: (deviceIds: string[]) => {
    const validIds = deviceIds.filter((id) => typeof id === 'string' && /^[\w-]{1,128}$/.test(id));
    if (validIds.length === 0) return;
    const { _ws } = get();
    set((state) => ({
      _subscribedDeviceIds: [...new Set([...state._subscribedDeviceIds, ...validIds])],
    }));
    if (_ws && _ws.readyState === WebSocket.OPEN) {
      _ws.send(JSON.stringify({ action: 'subscribeDevices', body: { deviceIds: validIds } }));
    }
  },

  unsubscribe: (deviceIds: string[]) => {
    const validIds = deviceIds.filter((id) => typeof id === 'string' && /^[\w-]{1,128}$/.test(id));
    if (validIds.length === 0) return;
    const { _ws } = get();
    set((state) => ({
      _subscribedDeviceIds: state._subscribedDeviceIds.filter((id) => !validIds.includes(id)),
    }));
    if (_ws && _ws.readyState === WebSocket.OPEN) {
      _ws.send(JSON.stringify({ action: 'unsubscribeDevices', body: { deviceIds: validIds } }));
    }
  },
}));

export function useDeviceData(deviceId: string): DeviceSensorPayload | null {
  return useWebSocketStore((state) => state.devices[deviceId] ?? null);
}

// Selector returns state.devices directly — reference equality is correct here.
// Re-renders only when the devices map is replaced (i.e. on any snapshot/update).
export function useAllDevices(): Record<string, DeviceSensorPayload> {
  return useWebSocketStore((state) => state.devices);
}

// useShallow prevents re-renders on device data updates when connected/reconnecting
// haven't actually changed (avoids creating a new object on every selector call).
export function useWebSocketStatus(): { connected: boolean; reconnecting: boolean } {
  return useWebSocketStore(
    useShallow((state) => ({ connected: state.connected, reconnecting: state.reconnecting })),
  );
}
